import { useAuth } from '@clerk/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/api/client';
import { createPortalNote, listPortalNotes, type EngagementNoteOut } from '@/api/portalNotes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/** Ad-hoc customer notes on the engagement — textarea to add one, plus a
 *  newest-first list. Seed of the future two-way thread. */
export function PortalNotesCard({ engagementId }: { engagementId: string }) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const notes = useQuery({
    queryKey: ['portal', 'notes', engagementId],
    queryFn: async () => {
      const token = (await getToken()) ?? '';
      return listPortalNotes(engagementId, token);
    },
  });

  const add = useMutation({
    mutationFn: async (body: string) => {
      const token = (await getToken()) ?? '';
      return createPortalNote(engagementId, body, token);
    },
    onSuccess: () => {
      setDraft('');
      setError(null);
      qc.invalidateQueries({ queryKey: ['portal', 'notes', engagementId] });
      qc.invalidateQueries({ queryKey: ['portal', 'checklist', engagementId] });
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiError
          ? `Couldn't save note (HTTP ${err.status}).`
          : "Couldn't save note.",
      );
    },
  });

  const trimmed = draft.trim();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notes</CardTitle>
        <CardDescription>
          Anything we should know — e.g. &ldquo;Q3 export excludes the US entity.&rdquo; Your
          InputRecovery team sees these.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        <div className="grid gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            placeholder="Add a note…"
            className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-brand-navy placeholder:text-brand-slate focus:border-brand-royal focus:outline-none"
          />
          <div className="flex items-center gap-3">
            <Button
              type="button"
              size="sm"
              onClick={() => add.mutate(trimmed)}
              disabled={trimmed.length === 0 || add.isPending}
            >
              {add.isPending ? 'Saving…' : 'Add note'}
            </Button>
            {error && <span className="text-xs text-destructive">{error}</span>}
          </div>
        </div>

        {notes.isPending ? (
          <p className="text-xs text-brand-slate">Loading notes…</p>
        ) : notes.isError ? (
          <p className="text-xs text-destructive">Couldn&apos;t load notes. Refresh to try again.</p>
        ) : notes.data.items.length === 0 ? (
          <p className="text-xs text-brand-slate">No notes yet.</p>
        ) : (
          <ul className="grid gap-2">
            {notes.data.items.map((note) => (
              <NoteRow key={note.id} note={note} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function NoteRow({ note }: { note: EngagementNoteOut }) {
  return (
    <li className="rounded-md border border-border px-3 py-2">
      <p className="whitespace-pre-wrap text-sm text-brand-navy">{note.body}</p>
      <p className="mt-1 text-xs text-brand-slate">
        {new Date(note.created_at).toLocaleString()}
      </p>
    </li>
  );
}
